import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, GraduationCap, LayoutGrid, Plus, Receipt, Wallet } from 'lucide-react';
import MetricCard from '../../components/MetricCard/MetricCard';
import PageEmptyState from '../../components/PageEmptyState/PageEmptyState';
import MyTasksWidget from '../../components/MyTasksWidget/MyTasksWidget';
import { getRevenueStats } from '../../api/stats';
import { getPaymentStats } from '../../api/payments';
import { isAccountAdmin } from '../../utils/scope';
import useMyCourseIds from '../../hooks/useMyCourseIds';
import { useAuth } from '../../context/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { useStudentSummary } from '../../components/Student360/useStudentSummary';
import { formatCompact, formatMoney, formatNumber } from '../../utils/datetime';
import { useI18n } from '../../i18n/I18nContext';
import StudentPulse from './StudentPulse';
import RevenueCard from './RevenueCard';
import RecentOrders from './RecentOrders';
import { QuickActions, TopCourses } from './RailCards';
import DashboardWelcome from './DashboardWelcome';
import { buildRevenueSeries } from './revenueSeries';
import './Dashboard.css';

const KPI_ICON_SIZE = 20;

/** The greeting for the browser's own time of day. */
function greetingKeyAt(now) {
  const hour = now.getHours();
  if (hour < 12) return 'greetingMorning';
  return hour < 18 ? 'greetingAfternoon' : 'greetingEvening';
}

/** A rejected request carries its HTTP status as `code` (the API client) or on `response`. */
function isForbidden(error) {
  return [error?.code, error?.status, error?.response?.status].some((status) => Number(status) === 403);
}

function DashboardPage({ onReload }) {
  const { t } = useI18n();
  const { courseIds, courses: myCourses, loading: scopeLoading, error: scopeError } = useMyCourseIds();
  const { user } = useAuth();
  const { canViewMoney, canSeeNav, canCreateCourse } = usePermissions();
  const accountAdmin = isAccountAdmin(user);
  // Account admins and course owners (teacher, mentor) have a catalogue to total. Every other staff
  // login (sales, call center, control ...) owns no course and its account is refused the order and
  // revenue routes, so its home is what it may open: the students, its tasks and the shortcuts.
  const ownsCourses = canCreateCourse();
  const showMoney = ownsCourses && canViewMoney();
  // How many students are online, active today, away or at risk: the students the signed-in account
  // may open, whatever its role. It has its own call, so the pulse is drawn even when the revenue
  // calls fail (an account without the money routes still gets its students).
  const studentSummary = useStudentSummary();

  const [period, setPeriod] = useState('30d');
  const [chartKey, setChartKey] = useState(0);
  const [stats, setStats] = useState({ status: 'loading', data: null, error: null });
  const [revenue, setRevenue] = useState({ status: 'loading', data: null });

  // Totals, top courses and newest orders: server aggregates over every order in scope. Account
  // admins see the whole tenant (the server scopes it); everyone else only the courses they own.
  // None of it depends on the period, so switching the period never reloads the page.
  useEffect(() => {
    if (!ownsCourses || scopeLoading) return undefined;
    let cancelled = false;
    setStats((prev) => ({ ...prev, status: 'loading', error: null }));

    const ownIds = accountAdmin ? null : [...(courseIds || [])];
    const request = ownIds && ownIds.length === 0
      ? Promise.resolve(null)
      : getPaymentStats(ownIds ? { courseIds: ownIds.join(',') } : {});
    request
      .then((data) => { if (!cancelled) setStats({ status: 'ready', data, error: null }); })
      .catch((error) => { if (!cancelled) setStats({ status: 'error', data: null, error }); });

    return () => { cancelled = true; };
  }, [ownsCourses, scopeLoading, courseIds, accountAdmin]);

  // The chart: only for roles that may see money, and only this card waits on the period.
  useEffect(() => {
    if (scopeLoading || !showMoney) return undefined;
    let cancelled = false;
    setRevenue((prev) => ({ ...prev, status: 'loading' }));

    getRevenueStats({ period })
      .then((data) => { if (!cancelled) setRevenue({ status: 'ready', data }); })
      .catch(() => { if (!cancelled) setRevenue({ status: 'error', data: null }); });

    return () => { cancelled = true; };
  }, [scopeLoading, showMoney, period, chartKey]);

  const series = useMemo(
    () => buildRevenueSeries(revenue.data?.revenueByDay, period),
    [revenue.data, period],
  );

  // The chart's own retry: only the chart asks again, the totals and the orders stay on screen.
  const reloadChart = () => setChartKey((key) => key + 1);

  const loading = ownsCourses && (scopeLoading || stats.status === 'loading');
  const failed = ownsCourses && !loading && (Boolean(scopeError) || stats.status === 'error');
  const totalCourses = myCourses.length;
  const isNewClient = ownsCourses && !loading && !failed && totalCourses === 0;

  const data = stats.data;
  const recentOrders = (data?.recentOrders || []).slice(0, 6);
  const topCourses = (data?.topCourses || []).slice(0, 4).map((course) => ({
    id: course.courseId,
    title: course.title || t('dashboard.untitledCourse'),
    revenue: Number(course.revenue) || 0,
  }));

  const first = (user?.name || '').trim().split(' ')[0];
  const greeting = t(`dashboard.${greetingKeyAt(new Date())}`);
  // One yellow primary per view: the welcome card brings its own.
  const canCreate = ownsCourses && canSeeNav('courses') && !isNewClient;

  const header = (
    <header className="page-head">
      <div className="page-head-main">
        <span className="page-head-icon" aria-hidden="true"><LayoutGrid /></span>
        <div className="page-head-text">
          <h1 className="page-title">{greeting}{first ? `, ${first}` : ''}</h1>
          {/* A client with no course has nothing to look at yet: the welcome card speaks instead. */}
          {!isNewClient && (
            <p className="page-sub">{t(ownsCourses ? 'dashboard.heroSubtitle' : 'dashboard.staffSubtitle')}</p>
          )}
        </div>
      </div>
      {canCreate && (
        <div className="page-actions">
          <Link to="/courses/create" className="ds-btn ds-btn-primary">
            <Plus aria-hidden="true" />
            {t('dashboard.createCourse')}
          </Link>
        </div>
      )}
    </header>
  );

  const pulse = <StudentPulse summary={studentSummary.data} loading={studentSummary.loading} />;

  if (!ownsCourses) {
    return (
      <div className="dashboard">
        {header}
        {pulse}
        <div className="dash-cards">
          <MyTasksWidget />
          <QuickActions />
        </div>
      </div>
    );
  }

  if (failed) {
    const forbidden = isForbidden(stats.error) || isForbidden(scopeError);
    return (
      <div className="dashboard">
        {header}
        {pulse}
        <PageEmptyState variant={forbidden ? 'forbidden' : 'error'} onRetry={forbidden ? undefined : onReload} />
      </div>
    );
  }

  if (isNewClient) {
    return (
      <div className="dashboard">
        {header}
        <DashboardWelcome />
      </div>
    );
  }

  const tiles = [
    {
      key: 'students',
      tone: 'info',
      icon: <GraduationCap size={KPI_ICON_SIZE} aria-hidden="true" />,
      value: formatNumber(data?.uniqueBuyers),
    },
    {
      key: 'orders',
      tone: 'brand',
      icon: <Receipt size={KPI_ICON_SIZE} aria-hidden="true" />,
      value: formatNumber(data?.completedOrders),
    },
    ...(showMoney ? [{
      key: 'revenue',
      tone: 'success',
      icon: <Wallet size={KPI_ICON_SIZE} aria-hidden="true" />,
      // The amount alone ("2.6 mln"): the unit sits in the hint, so a long figure never wraps in a narrow tile.
      value: formatCompact(Number(data?.revenue) || 0),
      full: formatMoney(Number(data?.revenue) || 0, { free: false }),
    }] : []),
    {
      key: 'courses',
      tone: 'purple',
      icon: <BookOpen size={KPI_ICON_SIZE} aria-hidden="true" />,
      value: formatNumber(totalCourses),
    },
  ];

  return (
    <div className="dashboard">
      {header}

      <div className="kpi-row">
        {tiles.map((tile) => (loading ? (
          // The skeleton and the real tile are different elements: the tile mounts with its figure
          // and the number counts up once, the way MetricCard does it everywhere.
          <MetricCard key={`${tile.key}-loading`} loading />
        ) : (
          <MetricCard
            key={tile.key}
            look="tinted"
            tone={tile.tone}
            icon={tile.icon}
            title={t(`dashboard.metrics.${tile.key}.title`)}
            value={tile.value}
            valueTitle={tile.full}
            change={t(`dashboard.metrics.${tile.key}.change`)}
          />
        )))}
      </div>

      {pulse}

      {showMoney ? (
        <div className="page-grid page-grid--rail">
          <div className="ui-stack">
            <RevenueCard
              period={period}
              onPeriodChange={setPeriod}
              status={revenue.status}
              series={series}
              onRetry={reloadChart}
            />
            <RecentOrders orders={recentOrders} loading={loading} />
          </div>
          <aside className="col-rail">
            <MyTasksWidget />
            <TopCourses courses={topCourses} loading={loading} />
          </aside>
        </div>
      ) : (
        <div className="dash-cards">
          <MyTasksWidget />
          <QuickActions />
        </div>
      )}
    </div>
  );
}

function Dashboard() {
  // A hard failure remounts the page, so a retry asks for everything again, the course scope included.
  const [attempt, setAttempt] = useState(0);
  return <DashboardPage key={attempt} onReload={() => setAttempt((n) => n + 1)} />;
}

export default Dashboard;
